import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Ticket,
  Plus,
  XCircle,
  Trash,
  CalendarBlank,
  WarningCircle,
  CheckCircle,
  Check,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  useGetPatientPunchCardsQuery,
  useGetPunchedAppointmentsQuery,
  useCreatePunchCardMutation,
  useClosePunchCardMutation,
  useDeletePunchCardMutation,
} from "../../../store/services/punchCardsApi";
import { useToast } from "../../../contexts/ToastContext";
import { formatHebrewDate, formatTime } from "../../../utils/dateUtils";
import ConfirmDialog from "../../ui/ConfirmDialog";
import PunchCardWizardModal from "./PunchCardWizardModal";
import "./PunchCardTab.css";
import ContentLoader from "../../shared/ContentLoader";

const STATUS_LABELS = {
  active: { label: "פעילה", className: "active" },
  used_up: { label: "נוצלה במלואה", className: "used-up" },
  expired: { label: "פגת תוקף", className: "expired" },
  closed: { label: "נסגרה", className: "closed" },
};

function PunchCardTab({ patientId, patient, canEdit }) {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();

  const patientName = patient
    ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
    : null;

  const [showWizard, setShowWizard] = useState(false);
  const [closingCard, setClosingCard] = useState(null);
  const [deletingCard, setDeletingCard] = useState(null);

  const { data: cards = [], isLoading } = useGetPatientPunchCardsQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  // The card shown in the hero: the active one, otherwise the most recent
  const heroCard = useMemo(
    () => cards.find((c) => c.status === "active") || cards[0] || null,
    [cards]
  );
  const historyCards = useMemo(
    () => cards.filter((c) => heroCard && c.id !== heroCard.id),
    [cards, heroCard]
  );

  const { data: punchedAppointments = [], isLoading: isLoadingPunched } =
    useGetPunchedAppointmentsQuery(
      { clinicId, cardId: heroCard?.id },
      { skip: !clinicId || !heroCard?.id }
    );

  const [createPunchCard, { isLoading: isCreating }] =
    useCreatePunchCardMutation();
  const [closePunchCard] = useClosePunchCardMutation();
  const [deletePunchCard] = useDeletePunchCardMutation();

  const handleCreate = async ({ cardData, recordPurchaseIncome }) => {
    try {
      await createPunchCard({
        clinicId,
        patientId,
        cardData: { ...cardData, patientName },
        recordPurchaseIncome,
        therapistId,
        therapistName,
      }).unwrap();
      setShowWizard(false);
      showSuccess("הכרטיסיה נוצרה בהצלחה");
    } catch (err) {
      showError(err?.message || "שגיאה ביצירת הכרטיסיה");
    }
  };

  const handleClose = async () => {
    try {
      await closePunchCard({
        clinicId,
        patientId,
        cardId: closingCard.id,
        therapistId,
        therapistName,
        patientName,
      }).unwrap();
      showSuccess("הכרטיסיה נסגרה");
    } catch (err) {
      showError(err?.message || "שגיאה בסגירת הכרטיסיה");
    }
  };

  const handleDelete = async () => {
    try {
      await deletePunchCard({
        clinicId,
        patientId,
        cardId: deletingCard.id,
        therapistId,
        therapistName,
        patientName,
      }).unwrap();
      showSuccess("הכרטיסיה נמחקה");
    } catch (err) {
      showError(err?.message || "שגיאה במחיקת הכרטיסיה");
    }
  };

  const renderCardSummary = (card) => {
    const remaining = Math.max(0, (card.totalVisits || 0) - (card.usedVisits || 0));
    const status = STATUS_LABELS[card.status] || STATUS_LABELS.active;
    return (
      <div className="punch-history-item" key={card.id}>
        <div className="punch-history-main">
          <Ticket size={18} weight="duotone" />
          <span className="punch-history-title">
            {card.totalVisits} מפגשים · ₪
            {(card.pricePerVisit || 0).toLocaleString("he-IL")} למפגש
          </span>
          <span className={`punch-status-badge ${status.className}`}>
            {status.label}
          </span>
        </div>
        <div className="punch-history-meta">
          <span>נרכשה: {formatHebrewDate(card.purchaseDate)}</span>
          <span>נוקבו: {card.usedVisits || 0}</span>
          {card.status !== "used_up" && remaining > 0 && (
            <span>לא נוצלו: {remaining}</span>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <ContentLoader fill />
    );
  }

  const heroRemaining = heroCard
    ? Math.max(0, (heroCard.totalVisits || 0) - (heroCard.usedVisits || 0))
    : 0;
  const heroStatus = heroCard
    ? STATUS_LABELS[heroCard.status] || STATUS_LABELS.active
    : null;

  return (
    <div className="punch-tab">
      <div className="punch-tab-header">
        <h3>
          <Ticket size={20} weight="duotone" />
          כרטיסיה
        </h3>
        {canEdit &&
          cards.length > 0 &&
          !cards.some((c) => c.status === "active") && (
            <button
              className="punch-btn primary"
              onClick={() => setShowWizard(true)}
            >
              <Plus size={16} weight="bold" />
              כרטיסיה חדשה
            </button>
          )}
      </div>

      {!heroCard ? (
        <div className="punch-empty-state">
          <div className="punch-empty-ticket" aria-hidden="true">
            <div className="punch-empty-ticket-main">
              <div className="punch-empty-holes">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={`punch-hole ${i < 2 ? "punched" : ""}`}
                  >
                    {i < 2 && <Check size={10} weight="bold" />}
                  </span>
                ))}
              </div>
              <span className="punch-empty-line long" />
              <span className="punch-empty-line short" />
            </div>
            <div className="punch-empty-ticket-stub">
              <Ticket size={18} weight="fill" />
            </div>
          </div>
          <h4>אין כרטיסיה למטופל</h4>
          <p>
            כרטיסיה מאפשרת למכור חבילת מפגשים מראש ולנקב מפגש בכל פגישה —
            ישירות מחלון קביעת הפגישה.
          </p>
          {canEdit && (
            <button
              className="punch-btn primary"
              onClick={() => setShowWizard(true)}
            >
              <Plus size={16} weight="bold" />
              צור כרטיסיה
            </button>
          )}
        </div>
      ) : (
        <>
          <div className={`punch-hero ${heroCard.status}`}>
            <div className="punch-hero-body">
              <div className="punch-hero-main">
                <div className="punch-hero-top">
                  <span
                    className={`punch-status-badge ${heroStatus.className}`}
                  >
                    {heroStatus.label}
                  </span>
                  <span className="punch-payment-badge">
                    {heroCard.paidUpfront ? "שולם מראש" : "תשלום פר מפגש"}
                  </span>
                  {canEdit && (
                    <div className="punch-hero-actions">
                      {(heroCard.status === "active" ||
                        heroCard.status === "used_up") && (
                        <button
                          data-tooltip="סגירת הכרטיסיה"
                          aria-label="סגירת הכרטיסיה"
                          onClick={() => setClosingCard(heroCard)}
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                      {(heroCard.usedVisits || 0) === 0 && (
                        <button
                          className="danger"
                          data-tooltip="מחיקת הכרטיסיה"
                          aria-label="מחיקת הכרטיסיה"
                          onClick={() => setDeletingCard(heroCard)}
                        >
                          <Trash size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {(heroCard.totalVisits || 0) > 0 &&
                heroCard.totalVisits <= 24 ? (
                  <div className="punch-holes">
                    {Array.from({ length: heroCard.totalVisits }).map(
                      (_, i) => (
                        <span
                          key={i}
                          className={`punch-hole ${
                            i < (heroCard.usedVisits || 0) ? "punched" : ""
                          }`}
                        >
                          {i < (heroCard.usedVisits || 0) && (
                            <Check size={11} weight="bold" />
                          )}
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <div className="punch-hero-progress">
                    <div
                      className="punch-hero-progress-fill"
                      style={{
                        width: `${
                          heroCard.totalVisits
                            ? Math.min(
                                100,
                                ((heroCard.usedVisits || 0) /
                                  heroCard.totalVisits) *
                                  100
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                )}

                <div className="punch-hero-details">
                  <span>
                    ₪{(heroCard.pricePerVisit || 0).toLocaleString("he-IL")}{" "}
                    למפגש
                  </span>
                  <span>
                    סה״כ ₪
                    {(
                      (heroCard.pricePerVisit || 0) *
                      (heroCard.totalVisits || 0)
                    ).toLocaleString("he-IL")}
                  </span>
                  <span>נרכשה: {formatHebrewDate(heroCard.purchaseDate)}</span>
                  {heroCard.expiryDate && (
                    <span>
                      בתוקף עד: {formatHebrewDate(heroCard.expiryDate)}
                    </span>
                  )}
                </div>
              </div>

              <div className="punch-hero-stub">
                <span className="punch-remaining">{heroRemaining}</span>
                <span className="punch-remaining-label">נותרו</span>
                <span className="punch-stub-total">
                  מתוך {heroCard.totalVisits}
                </span>
              </div>
            </div>

            {heroCard.status === "expired" && heroRemaining > 0 && (
              <div className="punch-hero-notice">
                <WarningCircle size={16} weight="fill" />
                הכרטיסיה פגה בתאריך {formatHebrewDate(heroCard.expiryDate)} —{" "}
                {heroRemaining} ניקובים שלא נוצלו פקעו.
              </div>
            )}
            {heroCard.paidUpfront && heroCard.purchaseIncomeRecorded && (
              <div className="punch-hero-note">
                <CheckCircle size={14} weight="fill" />
                הכנסת הרכישה נרשמה — פגישות מנוקבות נרשמות ב-₪0
              </div>
            )}
            {heroCard.notes && (
              <div className="punch-hero-notes">{heroCard.notes}</div>
            )}
          </div>

          <div className="punch-section">
            <h4>
              <CalendarBlank size={18} weight="duotone" />
              מפגשים שנוקבו ({punchedAppointments.length})
            </h4>
            {isLoadingPunched ? (
              <ContentLoader size="sm" compact />
            ) : punchedAppointments.length === 0 ? (
              <p className="punch-section-empty">
                עדיין לא נוקבו מפגשים בכרטיסיה זו. ניתן לסמן פגישה כ"מתוך
                כרטיסיה" בעת קביעת פגישה.
              </p>
            ) : (
              <ul className="punch-meetings-list">
                {punchedAppointments.map((apt) => (
                  <li key={apt.id}>
                    <span className="punch-meeting-date">
                      {formatHebrewDate(apt.startTime)} ·{" "}
                      {formatTime(apt.startTime)}
                    </span>
                    {apt.treatmentTypeName && (
                      <span className="punch-meeting-type">
                        {apt.treatmentTypeName}
                      </span>
                    )}
                    <span
                      className={`punch-meeting-status ${
                        apt.status === "cancelled"
                          ? "cancelled"
                          : apt.status === "completed"
                          ? "completed"
                          : "planned"
                      }`}
                    >
                      {apt.status === "completed"
                        ? "התקיימה"
                        : apt.status === "cancelled"
                        ? "בוטלה"
                        : "מתוכננת"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {historyCards.length > 0 && (
            <div className="punch-section">
              <h4>
                <Ticket size={18} weight="duotone" />
                כרטיסיות קודמות
              </h4>
              <div className="punch-history-list">
                {historyCards.map(renderCardSummary)}
              </div>
            </div>
          )}
        </>
      )}

      {showWizard && (
        <PunchCardWizardModal
          onClose={() => setShowWizard(false)}
          onCreate={handleCreate}
          isCreating={isCreating}
        />
      )}

      <ConfirmDialog
        isOpen={!!closingCard}
        onClose={() => setClosingCard(null)}
        onConfirm={handleClose}
        title="סגירת כרטיסיה"
        message="סגירת הכרטיסיה תבטל את הניקובים שנותרו. פגישות שכבר נוקבו לא יושפעו. להמשיך?"
        confirmText="סגור כרטיסיה"
        type="warning"
      />

      <ConfirmDialog
        isOpen={!!deletingCard}
        onClose={() => setDeletingCard(null)}
        onConfirm={handleDelete}
        title="מחיקת כרטיסיה"
        message="הכרטיסיה תימחק לצמיתות. פעולה זו אפשרית רק כי טרם נוקבו בה מפגשים. להמשיך?"
        confirmText="מחק"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default PunchCardTab;
